import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Page,
  Layout,
  Card,
  Text,
  TextField,
  Button,
  FormLayout,
  Checkbox,
  Select,
  Tabs,
  Badge,
  Spinner,
  Divider
} from '@shopify/polaris'
import toast from 'react-hot-toast'

import { useAuth } from '@/contexts/AuthContext'
import { storeApi, whatsappApi, shopifyApi, billingApi } from '@/services/api'
import type { StoreSettings, WhatsAppProfile } from '@/types'

export default function SettingsPage() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [selectedTab, setSelectedTab] = useState(0)
  const [saving, setSaving] = useState(false)

  const { data: storeData, isLoading: storeLoading } = useQuery({
    queryKey: ['store', user?.storeId],
    queryFn: () => storeApi.getStore(user!.storeId),
    enabled: !!user?.storeId
  })

  const { data: whatsappProfileData, isLoading: profileLoading } = useQuery({
    queryKey: ['whatsapp-profile', user?.storeId],
    queryFn: () => whatsappApi.getProfile(user!.storeId),
    enabled: !!user?.storeId && storeData?.data.data.store.hasWhatsappIntegration
  })

  const { data: shopifyInfoData } = useQuery({
    queryKey: ['shopify-info', user?.storeId],
    queryFn: () => shopifyApi.getStoreInfo(user!.storeId),
    enabled: !!user?.storeId && storeData?.data.data.store.hasShopifyIntegration
  })

  const { data: subscriptionData, isLoading: subscriptionLoading } = useQuery({
    queryKey: ['subscription-status', user?.storeId],
    queryFn: () => billingApi.getStatus(user!.storeId),
    enabled: !!user?.storeId
  })

  const updateSettingsMutation = useMutation({
    mutationFn: ({ storeId, settings }: { storeId: string; settings: StoreSettings }) =>
      storeApi.updateSettings(storeId, settings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store'] })
      toast.success('Settings updated successfully')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Failed to update settings')
    }
  })

  const updateWhatsAppMutation = useMutation({
    mutationFn: ({ storeId, data }: { storeId: string; data: any }) =>
      storeApi.updateWhatsApp(storeId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store'] })
      toast.success('WhatsApp integration updated successfully')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Failed to update WhatsApp integration')
    }
  })

  const updateProfileMutation = useMutation({
    mutationFn: ({ storeId, profile }: { storeId: string; profile: WhatsAppProfile }) =>
      whatsappApi.updateProfile(storeId, profile),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['whatsapp-profile'] })
      toast.success('WhatsApp profile updated successfully')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Failed to update profile')
    }
  })

  const syncShopifyMutation = useMutation({
    mutationFn: ({ storeId, syncType }: { storeId: string; syncType: string }) =>
      shopifyApi.syncData(storeId, syncType),
    onSuccess: () => {
      toast.success('Shopify data synced successfully')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Failed to sync data')
    }
  })

  const cancelSubscriptionMutation = useMutation({
    mutationFn: (storeId: string) => billingApi.cancelSubscription(storeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription-status'] })
      toast.success('Subscription cancelled successfully')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Failed to cancel subscription')
    }
  })

  const store = storeData?.data.data.store
  const whatsappProfile = whatsappProfileData?.data.data.profile
  const shopifyInfo = shopifyInfoData?.data.data.shopInfo
  const subscription = subscriptionData?.data.data

  const [storeSettings, setStoreSettings] = useState<StoreSettings>({
    businessHours: {
      enabled: false,
      schedule: {
        monday: { start: '09:00', end: '17:00', enabled: true },
        tuesday: { start: '09:00', end: '17:00', enabled: true },
        wednesday: { start: '09:00', end: '17:00', enabled: true },
        thursday: { start: '09:00', end: '17:00', enabled: true },
        friday: { start: '09:00', end: '17:00', enabled: true },
        saturday: { start: '09:00', end: '17:00', enabled: false },
        sunday: { start: '09:00', end: '17:00', enabled: false }
      }
    },
    autoReply: {
      enabled: false,
      businessHours: 'Thank you for contacting us! We\'ll respond during business hours.',
      afterHours: 'Thank you for your message. We\'re currently closed but will respond when we\'re back.'
    },
    notifications: {
      email: true,
      browser: true,
      sound: true
    },
    ...store?.settings
  })

  const [whatsappSettings, setWhatsappSettings] = useState({
    whatsappBusinessId: '',
    whatsappPhoneNumber: '',
    whatsappAccessToken: ''
  })

  const [profileSettings, setProfileSettings] = useState<WhatsAppProfile>({
    about: '',
    description: '',
    email: '',
    websites: [],
    address: '',
    ...whatsappProfile
  })

  const handleSaveSettings = async () => {
    if (!user?.storeId) return
    
    setSaving(true)
    try {
      await updateSettingsMutation.mutateAsync({
        storeId: user.storeId,
        settings: storeSettings
      })
    } finally {
      setSaving(false)
    }
  }

  const handleSaveWhatsApp = async () => {
    if (!user?.storeId) return
    
    setSaving(true)
    try {
      await updateWhatsAppMutation.mutateAsync({
        storeId: user.storeId,
        data: whatsappSettings
      })
    } finally {
      setSaving(false)
    }
  }

  const handleSaveProfile = async () => {
    if (!user?.storeId) return
    
    setSaving(true)
    try {
      await updateProfileMutation.mutateAsync({
        storeId: user.storeId,
        profile: profileSettings
      })
    } finally {
      setSaving(false)
    }
  }

  const handleSyncShopify = async (syncType: string = 'all') => {
    if (!user?.storeId) return

    await syncShopifyMutation.mutateAsync({
      storeId: user.storeId,
      syncType
    })
  }

  const handleCancelSubscription = async () => {
    if (!user?.storeId) return

    if (confirm('Are you sure you want to cancel your subscription? You will lose access to premium features.')) {
      await cancelSubscriptionMutation.mutateAsync(user.storeId)
    }
  }

  const tabs = [
    {
      id: 'general',
      content: 'General',
      panelID: 'general-panel'
    },
    {
      id: 'subscription',
      content: 'Subscription',
      panelID: 'subscription-panel'
    },
    {
      id: 'whatsapp',
      content: 'WhatsApp',
      panelID: 'whatsapp-panel'
    },
    {
      id: 'shopify',
      content: 'Shopify',
      panelID: 'shopify-panel'
    },
    {
      id: 'automation',
      content: 'Automation',
      panelID: 'automation-panel'
    }
  ]

  if (storeLoading) {
    return (
      <Page title="Settings">
        <Layout>
          <Layout.Section>
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
              <Spinner accessibilityLabel="Loading settings" size="large" />
            </div>
          </Layout.Section>
        </Layout>
      </Page>
    )
  }

  return (
    <Page title="Settings" subtitle="Manage your WhatsApp CRM configuration">
      <Layout>
        <Layout.Section>
          <Card>
            <Tabs tabs={tabs} selected={selectedTab} onSelect={setSelectedTab}>
              <div style={{ padding: '16px' }}>
                {selectedTab === 0 && (
                  <div>
                    <Text variant="headingMd" as="h3">
                      General Settings
                    </Text>
                    
                    <div style={{ marginTop: '24px' }}>
                      <FormLayout>
                        <div>
                          <Text variant="bodyMd" fontWeight="semibold" as="p">
                            Store Information
                          </Text>
                          <div style={{ marginTop: '12px' }}>
                            <TextField
                              label="Store Domain"
                              value={store?.shopDomain || ''}
                              disabled
                              helpText="Your Shopify store domain"
                            />
                          </div>
                          <div style={{ marginTop: '12px' }}>
                            <Badge status={store?.status === 'ACTIVE' ? 'success' : 'critical'}>
                              {store?.status || 'Unknown'}
                            </Badge>
                          </div>
                        </div>

                        <Divider />

                        <div>
                          <Text variant="bodyMd" fontWeight="semibold" as="p">
                            Notifications
                          </Text>
                          <div style={{ marginTop: '12px' }}>
                            <Checkbox
                              label="Email notifications"
                              checked={storeSettings.notifications?.email || false}
                              onChange={(checked) => 
                                setStoreSettings(prev => ({
                                  ...prev,
                                  notifications: { ...prev.notifications, email: checked }
                                }))
                              }
                            />
                          </div>
                          <div style={{ marginTop: '8px' }}>
                            <Checkbox
                              label="Browser notifications"
                              checked={storeSettings.notifications?.browser || false}
                              onChange={(checked) => 
                                setStoreSettings(prev => ({
                                  ...prev,
                                  notifications: { ...prev.notifications, browser: checked }
                                }))
                              }
                            />
                          </div>
                          <div style={{ marginTop: '8px' }}>
                            <Checkbox
                              label="Sound notifications"
                              checked={storeSettings.notifications?.sound || false}
                              onChange={(checked) => 
                                setStoreSettings(prev => ({
                                  ...prev,
                                  notifications: { ...prev.notifications, sound: checked }
                                }))
                              }
                            />
                          </div>
                        </div>

                        <div style={{ marginTop: '24px' }}>
                          <Button primary onClick={handleSaveSettings} loading={saving}>
                            Save Settings
                          </Button>
                        </div>
                      </FormLayout>
                    </div>
                  </div>
                )}

                {selectedTab === 1 && (
                  <div>
                    <Text variant="headingMd" as="h3">
                      Subscription & Billing
                    </Text>

                    {subscriptionLoading ? (
                      <div style={{ textAlign: 'center', padding: '40px 0' }}>
                        <Spinner size="large" />
                      </div>
                    ) : subscription?.hasSubscription ? (
                      <div style={{ marginTop: '24px' }}>
                        <FormLayout>
                          <div>
                            <Text variant="bodyMd" fontWeight="semibold" as="p">
                              Current Plan
                            </Text>
                            <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                              <Text variant="headingLg" as="p">
                                {subscription.plan || 'N/A'}
                              </Text>
                              <Badge tone={subscription.isActive ? 'success' : 'critical'}>
                                {subscription.isActive ? 'Active' : 'Inactive'}
                              </Badge>
                            </div>
                          </div>

                          {subscription.trialEndsOn && (
                            <div>
                              <Text variant="bodyMd" fontWeight="semibold" as="p">
                                Trial Period
                              </Text>
                              <Text variant="bodyMd" as="p">
                                Your trial ends on {new Date(subscription.trialEndsOn).toLocaleDateString()}
                              </Text>
                            </div>
                          )}

                          <Divider />

                          <div>
                            <Text variant="bodyMd" fontWeight="semibold" as="p">
                              Plan Features
                            </Text>
                            {subscription.features && (
                              <div style={{ marginTop: '12px' }}>
                                <ul style={{ paddingLeft: '20px', marginTop: '8px' }}>
                                  {subscription.features.maxConversations && (
                                    <li>
                                      <Text as="p" variant="bodyMd">
                                        Up to {subscription.features.maxConversations.toLocaleString()} conversations/month
                                      </Text>
                                    </li>
                                  )}
                                  {subscription.features.maxAgentSeats && (
                                    <li>
                                      <Text as="p" variant="bodyMd">
                                        {subscription.features.maxAgentSeats} agent seats
                                      </Text>
                                    </li>
                                  )}
                                  {subscription.features.automation && (
                                    <li>
                                      <Text as="p" variant="bodyMd">
                                        {subscription.features.automation.charAt(0).toUpperCase() + subscription.features.automation.slice(1)} automation
                                      </Text>
                                    </li>
                                  )}
                                  {subscription.features.campaigns && (
                                    <li>
                                      <Text as="p" variant="bodyMd">
                                        Campaign management
                                      </Text>
                                    </li>
                                  )}
                                  {subscription.features.analytics && (
                                    <li>
                                      <Text as="p" variant="bodyMd">
                                        {subscription.features.analytics.charAt(0).toUpperCase() + subscription.features.analytics.slice(1)} analytics
                                      </Text>
                                    </li>
                                  )}
                                  {subscription.features.support && (
                                    <li>
                                      <Text as="p" variant="bodyMd">
                                        {subscription.features.support.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())} support
                                      </Text>
                                    </li>
                                  )}
                                </ul>
                              </div>
                            )}
                          </div>

                          <Divider />

                          <div>
                            <Text variant="bodyMd" fontWeight="semibold" as="p" tone="critical">
                              Cancel Subscription
                            </Text>
                            <div style={{ marginTop: '12px' }}>
                              <Text as="p" variant="bodyMd" tone="subdued">
                                Cancelling your subscription will immediately revoke access to all premium features. Your data will be preserved for 30 days.
                              </Text>
                              <div style={{ marginTop: '12px' }}>
                                <Button
                                  tone="critical"
                                  onClick={handleCancelSubscription}
                                  loading={cancelSubscriptionMutation.isPending}
                                >
                                  Cancel Subscription
                                </Button>
                              </div>
                            </div>
                          </div>
                        </FormLayout>
                      </div>
                    ) : (
                      <div style={{ marginTop: '24px' }}>
                        <Card>
                          <div style={{ padding: '20px', textAlign: 'center' }}>
                            <Text variant="headingMd" as="p">
                              No Active Subscription
                            </Text>
                            <div style={{ marginTop: '12px' }}>
                              <Text as="p" variant="bodyMd" tone="subdued">
                                Subscribe to a plan to access all features of WhatsApp CRM
                              </Text>
                            </div>
                            <div style={{ marginTop: '20px' }}>
                              <Button
                                variant="primary"
                                onClick={() => window.location.href = `/billing/select-plan?store=${user?.storeId}`}
                              >
                                View Plans
                              </Button>
                            </div>
                          </div>
                        </Card>
                      </div>
                    )}
                  </div>
                )}

                {selectedTab === 2 && (
                  <div>
                    <Text variant="headingMd" as="h3">
                      WhatsApp Integration
                    </Text>
                    
                    <div style={{ marginTop: '16px' }}>
                      <Badge status={store?.hasWhatsappIntegration ? 'success' : 'critical'}>
                        {store?.hasWhatsappIntegration ? 'Connected' : 'Not Connected'}
                      </Badge>
                    </div>

                    <div style={{ marginTop: '24px' }}>
                      <FormLayout>
                        <TextField
                          label="Business Account ID"
                          value={whatsappSettings.whatsappBusinessId}
                          onChange={(value) => 
                            setWhatsappSettings(prev => ({ ...prev, whatsappBusinessId: value }))
                          }
                          placeholder="Enter your WhatsApp Business Account ID"
                          helpText="Found in your Facebook Business Manager"
                        />
                        
                        <TextField
                          label="Phone Number ID"
                          value={whatsappSettings.whatsappPhoneNumber}
                          onChange={(value) => 
                            setWhatsappSettings(prev => ({ ...prev, whatsappPhoneNumber: value }))
                          }
                          placeholder="Enter your WhatsApp Phone Number ID"
                        />
                        
                        <TextField
                          label="Access Token"
                          type="password"
                          value={whatsappSettings.whatsappAccessToken}
                          onChange={(value) => 
                            setWhatsappSettings(prev => ({ ...prev, whatsappAccessToken: value }))
                          }
                          placeholder="Enter your WhatsApp Access Token"
                          helpText="Keep this secure and never share it"
                        />

                        <div style={{ marginTop: '24px' }}>
                          <Button primary onClick={handleSaveWhatsApp} loading={saving}>
                            Save WhatsApp Settings
                          </Button>
                        </div>
                      </FormLayout>
                    </div>

                    {store?.hasWhatsappIntegration && (
                      <>
                        <Divider />
                        <div style={{ marginTop: '24px' }}>
                          <Text variant="bodyMd" fontWeight="semibold" as="p">
                            Business Profile
                          </Text>
                          
                          <div style={{ marginTop: '16px' }}>
                            <FormLayout>
                              <TextField
                                label="About"
                                value={profileSettings.about || ''}
                                onChange={(value) => 
                                  setProfileSettings(prev => ({ ...prev, about: value }))
                                }
                                placeholder="Brief description of your business"
                                multiline={2}
                              />
                              
                              <TextField
                                label="Description"
                                value={profileSettings.description || ''}
                                onChange={(value) => 
                                  setProfileSettings(prev => ({ ...prev, description: value }))
                                }
                                placeholder="Detailed business description"
                                multiline={3}
                              />
                              
                              <TextField
                                label="Email"
                                type="email"
                                value={profileSettings.email || ''}
                                onChange={(value) => 
                                  setProfileSettings(prev => ({ ...prev, email: value }))
                                }
                                placeholder="Business email address"
                              />
                              
                              <TextField
                                label="Address"
                                value={profileSettings.address || ''}
                                onChange={(value) => 
                                  setProfileSettings(prev => ({ ...prev, address: value }))
                                }
                                placeholder="Business address"
                                multiline={2}
                              />

                              <div style={{ marginTop: '16px' }}>
                                <Button onClick={handleSaveProfile} loading={saving}>
                                  Update Profile
                                </Button>
                              </div>
                            </FormLayout>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {selectedTab === 3 && (
                  <div>
                    <Text variant="headingMd" as="h3">
                      Shopify Integration
                    </Text>

                    <div style={{ marginTop: '16px' }}>
                      <Badge status={store?.hasShopifyIntegration ? 'success' : 'critical'}>
                        {store?.hasShopifyIntegration ? 'Connected' : 'Not Connected'}
                      </Badge>
                    </div>

                    {store?.hasShopifyIntegration && shopifyInfo && (
                      <div style={{ marginTop: '24px' }}>
                        <FormLayout>
                          <div>
                            <Text variant="bodyMd" fontWeight="semibold" as="p">
                              Store Information
                            </Text>
                            <div style={{ marginTop: '12px' }}>
                              <Text variant="bodyMd" as="p">
                                <strong>Name:</strong> {shopifyInfo.name}
                              </Text>
                              <Text variant="bodyMd" as="p">
                                <strong>Email:</strong> {shopifyInfo.email}
                              </Text>
                              <Text variant="bodyMd" as="p">
                                <strong>Domain:</strong> {shopifyInfo.domain}
                              </Text>
                              <Text variant="bodyMd" as="p">
                                <strong>Currency:</strong> {shopifyInfo.currency}
                              </Text>
                            </div>
                          </div>

                          <Divider />

                          <div>
                            <Text variant="bodyMd" fontWeight="semibold" as="p">
                              Data Synchronization
                            </Text>
                            <div style={{ marginTop: '12px', display: 'flex', gap: '8px' }}>
                              <Button onClick={() => handleSyncShopify('customers')}>
                                Sync Customers
                              </Button>
                              <Button onClick={() => handleSyncShopify('orders')}>
                                Sync Orders
                              </Button>
                              <Button onClick={() => handleSyncShopify('all')}>
                                Sync All Data
                              </Button>
                            </div>
                          </div>
                        </FormLayout>
                      </div>
                    )}
                  </div>
                )}

                {selectedTab === 4 && (
                  <div>
                    <Text variant="headingMd" as="h3">
                      Automation Settings
                    </Text>
                    
                    <div style={{ marginTop: '24px' }}>
                      <FormLayout>
                        <div>
                          <Checkbox
                            label="Enable business hours"
                            checked={storeSettings.businessHours?.enabled || false}
                            onChange={(checked) => 
                              setStoreSettings(prev => ({
                                ...prev,
                                businessHours: { ...prev.businessHours, enabled: checked }
                              }))
                            }
                            helpText="Automatically handle messages based on business hours"
                          />
                        </div>

                        <div>
                          <Checkbox
                            label="Enable auto-reply"
                            checked={storeSettings.autoReply?.enabled || false}
                            onChange={(checked) => 
                              setStoreSettings(prev => ({
                                ...prev,
                                autoReply: { ...prev.autoReply, enabled: checked }
                              }))
                            }
                            helpText="Send automatic replies to new messages"
                          />
                        </div>

                        {storeSettings.autoReply?.enabled && (
                          <>
                            <TextField
                              label="Business Hours Message"
                              value={storeSettings.autoReply?.businessHours || ''}
                              onChange={(value) => 
                                setStoreSettings(prev => ({
                                  ...prev,
                                  autoReply: { ...prev.autoReply, businessHours: value }
                                }))
                              }
                              placeholder="Message sent during business hours"
                              multiline={3}
                            />
                            
                            <TextField
                              label="After Hours Message"
                              value={storeSettings.autoReply?.afterHours || ''}
                              onChange={(value) => 
                                setStoreSettings(prev => ({
                                  ...prev,
                                  autoReply: { ...prev.autoReply, afterHours: value }
                                }))
                              }
                              placeholder="Message sent outside business hours"
                              multiline={3}
                            />
                          </>
                        )}

                        <div style={{ marginTop: '24px' }}>
                          <Button primary onClick={handleSaveSettings} loading={saving}>
                            Save Automation Settings
                          </Button>
                        </div>
                      </FormLayout>
                    </div>
                  </div>
                )}
              </div>
            </Tabs>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  )
}